import { Reducer } from "react";

// SingleRoleRoulette 게임 상태 관리 리듀서
// 1인용으로 서버 통신 없이 로컬에서 모든 처리

export interface SpecialRole {
  name: string;
  count: number;
  explain: string;
}

export interface DefaultRole {
  name: string;
  explain: string;
}

export interface PlayerAssignment {
  playerNumber: number; // 1, 2, 3...
  role: string;
  isCurrentPlayer?: boolean; // 룰렛 모드에서 현재 보고 있는 플레이어
}

// 비공개 찬반투표 타입
export interface SimpleVote {
  playerNumber: number;
  choice: "agree" | "disagree"; // 찬성/반대
  timestamp: number;
}

export interface SimpleVoteSession {
  totalVoters: number; // 투표 인원 수
  votes: SimpleVote[];
  currentVoterIndex: number; // 현재 투표 중인 순서 (0, 1, 2...)
  completed: boolean;
  results?: {
    agree: number;
    disagree: number;
  };
}

export enum GameMode {
  ROULETTE = "roulette", // 룰렛 모드 - 한 명씩 확인
  MODERATOR = "moderator", // 사회자 모드 - 전체 결과 보기
}

export interface SingleRoleRouletteState {
  // 게임 설정
  labels: SpecialRole[];
  defaultLabel: DefaultRole;
  totalPlayers: number; // 총 인원 수
  gameMode: GameMode;

  // 게임 상태
  settingsCompleted: boolean;
  started: boolean;

  // 결과
  assignments: PlayerAssignment[];
  currentPlayerIndex: number; // 룰렛 모드에서 현재 확인 중인 플레이어 인덱스

  // 비공개 찬반투표
  simpleVoteSession: SimpleVoteSession | null;
}

export type SingleRoleRouletteAction =
  | { type: "ADD_LABEL"; label: SpecialRole }
  | { type: "REMOVE_LABEL"; name: string }
  | { type: "UPDATE_LABEL"; name: string; count: number; explain: string }
  | { type: "SET_LABELS"; labels: SpecialRole[] }
  | { type: "SET_DEFAULT_LABEL"; defaultLabel: DefaultRole }
  | { type: "SET_TOTAL_PLAYERS"; count: number }
  | { type: "SET_GAME_MODE"; mode: GameMode }
  | { type: "SET_SETTINGS_COMPLETED"; completed: boolean }
  | { type: "START_GAME" } // 게임 시작 및 역할 분배
  | { type: "NEXT_PLAYER" } // 룰렛 모드에서 다음 플레이어로
  | { type: "PREVIOUS_PLAYER" } // 룰렛 모드에서 이전 플레이어로
  | { type: "SET_CURRENT_PLAYER"; index: number }
  | { type: "END_DISTRIBUTION" } // 분배 종료 - 역할 설정은 유지하면서 게임 상태만 초기화
  | { type: "RESET" }
  | { type: "START_SIMPLE_VOTE"; totalVoters: number } // 비공개 찬반투표 시작
  | { type: "CAST_SIMPLE_VOTE"; playerNumber: number; choice: "agree" | "disagree" } // 투표하기
  | { type: "NEXT_SIMPLE_VOTER" } // 다음 투표자로
  | { type: "SHOW_SIMPLE_VOTE_RESULTS" } // 투표 결과 계산 및 표시
  | { type: "END_SIMPLE_VOTE" }; // 투표 종료

export const initialSingleRoleRouletteState: SingleRoleRouletteState = {
  labels: [],
  defaultLabel: { name: "", explain: "" },
  totalPlayers: 4,
  gameMode: GameMode.ROULETTE,
  settingsCompleted: false,
  started: false,
  assignments: [],
  currentPlayerIndex: 0,
  simpleVoteSession: null,
};

// 역할 분배 로직
const distributeRoles = (
  totalPlayers: number,
  specialRoles: SpecialRole[],
  defaultRole: DefaultRole
): PlayerAssignment[] => {
  const assignments: PlayerAssignment[] = [];
  const rolePool: string[] = [];

  // 특수 역할들을 풀에 추가
  specialRoles.forEach((role) => {
    for (let i = 0; i < role.count; i++) {
      rolePool.push(role.name);
    }
  });

  // 나머지는 기본 역할로 채우기
  const remainingSlots = totalPlayers - rolePool.length;
  for (let i = 0; i < remainingSlots; i++) {
    rolePool.push(defaultRole.name);
  }

  // 역할 섞기 (Fisher-Yates shuffle)
  for (let i = rolePool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [rolePool[i], rolePool[j]] = [rolePool[j], rolePool[i]];
  }

  // 플레이어에게 역할 배정
  for (let i = 0; i < totalPlayers; i++) {
    assignments.push({
      playerNumber: i + 1,
      role: rolePool[i] || defaultRole.name,
    });
  }

  return assignments;
};

export const singleRoleRouletteReducer: Reducer<
  SingleRoleRouletteState,
  SingleRoleRouletteAction
> = (state, action) => {
  switch (action.type) {
    case "ADD_LABEL":
      return {
        ...state,
        labels: [...state.labels, action.label],
      };
    case "REMOVE_LABEL":
      return {
        ...state,
        labels: state.labels.filter((label) => label.name !== action.name),
      };
    case "UPDATE_LABEL":
      return {
        ...state,
        labels: state.labels.map((label) =>
          label.name === action.name
            ? { ...label, count: action.count, explain: action.explain }
            : label
        ),
      };
    case "SET_LABELS":
      return {
        ...state,
        labels: action.labels,
      };
    case "SET_DEFAULT_LABEL":
      return {
        ...state,
        defaultLabel: action.defaultLabel,
      };
    case "SET_TOTAL_PLAYERS":
      return {
        ...state,
        totalPlayers: Math.max(1, action.count),
      };
    case "SET_GAME_MODE":
      return {
        ...state,
        gameMode: action.mode,
      };
    case "SET_SETTINGS_COMPLETED":
      return {
        ...state,
        settingsCompleted: action.completed,
      };
    case "START_GAME":
      const assignments = distributeRoles(
        state.totalPlayers,
        state.labels,
        state.defaultLabel
      );
      return {
        ...state,
        started: true,
        assignments,
        currentPlayerIndex: 0,
      };
    case "NEXT_PLAYER":
      return {
        ...state,
        currentPlayerIndex: Math.min(
          state.currentPlayerIndex + 1,
          state.assignments.length - 1
        ),
      };
    case "PREVIOUS_PLAYER":
      return {
        ...state,
        currentPlayerIndex: Math.max(0, state.currentPlayerIndex - 1),
      };
    case "SET_CURRENT_PLAYER":
      return {
        ...state,
        currentPlayerIndex: Math.max(
          0,
          Math.min(action.index, state.assignments.length - 1)
        ),
      };
    case "END_DISTRIBUTION":
      return {
        ...state,
        started: false,
        assignments: [],
        currentPlayerIndex: 0,
      };
    case "RESET":
      return initialSingleRoleRouletteState;
    case "START_SIMPLE_VOTE":
      return {
        ...state,
        simpleVoteSession: {
          totalVoters: action.totalVoters,
          votes: [],
          currentVoterIndex: 0,
          completed: false,
        },
      };
    case "CAST_SIMPLE_VOTE":
      if (!state.simpleVoteSession) return state;
      return {
        ...state,
        simpleVoteSession: {
          ...state.simpleVoteSession,
          votes: [
            ...state.simpleVoteSession.votes,
            {
              playerNumber: action.playerNumber,
              choice: action.choice,
              timestamp: Date.now(),
            },
          ],
        },
      };
    case "NEXT_SIMPLE_VOTER":
      if (!state.simpleVoteSession) return state;
      return {
        ...state,
        simpleVoteSession: {
          ...state.simpleVoteSession,
          currentVoterIndex: Math.min(
            state.simpleVoteSession.currentVoterIndex + 1,
            state.simpleVoteSession.totalVoters - 1
          ),
        },
      };
    case "SHOW_SIMPLE_VOTE_RESULTS":
      if (!state.simpleVoteSession) return state;
      const agreeCount = state.simpleVoteSession.votes.filter(
        (v) => v.choice === "agree"
      ).length;
      const disagreeCount = state.simpleVoteSession.votes.filter(
        (v) => v.choice === "disagree"
      ).length;
      return {
        ...state,
        simpleVoteSession: {
          ...state.simpleVoteSession,
          completed: true,
          results: {
            agree: agreeCount,
            disagree: disagreeCount,
          },
        },
      };
    case "END_SIMPLE_VOTE":
      return {
        ...state,
        simpleVoteSession: null,
      };
    default:
      return state;
  }
};
